import { Badge, HStack, Link, Text, VStack } from '@chakra-ui/react'
import { formatPhone, telHref } from '../../../../lib/contactMethods'
import type { TurfContactDto } from '../../../../types/canvasser'
import type { ContactDetail } from '../../../../types/contactDetail'
import { lastVisitHint } from '../WhoAnsweredStep'
import { InfoRow, WorkSection } from './WorkSection'

/**
 * [Spec §4.7, P5-6] Walk-list fields first (they work offline), then what only the server knows
 * (email, voter info) once it arrives. The field set is C1's call; [CANV-10] individual partisan and
 * turnout scores are never shown, even if a value slips through.
 */
export function DetailsSection({ person, detail }: { person: TurfContactDto; detail: ContactDetail | null }) {
  const phone = person.phone ?? detail?.phone ?? null
  const street = person.homeStreetAddress ?? person.streetAddress
  const place = [person.homeCity ?? person.city, person.homeZip ?? person.zip].filter(Boolean).join(' ')
  const hint = lastVisitHint(person)
  const dnc = person.isDoNotContact || !!detail?.isDoNotContact
  const v = detail?.voterRecord
  return (
    <VStack align="stretch" gap={3}>
      <HStack justify="space-between" align="flex-start">
        <Text fontSize="lg" fontWeight="bold">{`${person.firstName} ${person.lastName ?? ''}`.trim()}</Text>
        {dnc && <Badge bg="danger.subtle" color="danger.fg">Do not contact</Badge>}
      </HStack>
      {street && (
        <VStack align="stretch" gap={0}>
          <Text fontSize="sm">{person.unitNumber ? `${street} ${person.unitNumber}` : street}</Text>
          {place && <Text fontSize="sm" color="fg.muted">{place}</Text>}
        </VStack>
      )}
      {phone && (
        <Link href={telHref(phone)} color="accent" fontWeight="medium" userSelect="text" minH="44px" display="inline-flex" alignItems="center">
          {formatPhone(phone)}
        </Link>
      )}
      {detail?.email && (
        <Link href={`mailto:${detail.email}`} color="accent" fontWeight="medium" userSelect="text" minH="44px" display="inline-flex" alignItems="center">
          {detail.email}
        </Link>
      )}
      {hint && <Text fontSize="sm" color="fg.muted">{`Last visit: ${hint}`}</Text>}
      <WorkSection person={person} />
      {v && (
        <VStack align="stretch" gap={1}>
          <InfoRow label="Party" value={v.party} />
          <InfoRow label="Registration" value={v.registrationStatus} />
          <InfoRow label="Precinct" value={v.precinctName || v.precinct} />
          <InfoRow label="Best time" value={v.bestContactTime} />
        </VStack>
      )}
    </VStack>
  )
}
